/**
 * Utility functions for formatting numbers and currencies
 */

/**
 * Formats a number into a more readable format with K, M, B suffixes
 * Hides decimal places when they are 0
 * @param value - The number to format
 * @returns Formatted string (e.g., "1.5M", "2B", "500K")
 */
export const formatLargeNumber = (value: number): string => {
  const formatWithOptionalDecimal = (num: number): string => {
    const rounded = Math.round(num * 10) / 10;
    return rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1);
  };

  if (value >= 1e9) {
    return `${formatWithOptionalDecimal(value / 1e9)}B`;
  } else if (value >= 1e6) {
    return `${formatWithOptionalDecimal(value / 1e6)}M`;
  } else if (value >= 1e3) {
    return `${formatWithOptionalDecimal(value / 1e3)}K`;
  }
  return value.toLocaleString();
};

/**
 * Formats a currency value with proper number formatting and currency symbol
 * @param amount - The amount to format
 * @param currency - The currency code (e.g., "USD", "EUR")
 * @returns Formatted currency string (e.g., "$1.5M USD", "€2.3B EUR")
 */
export const formatCurrency = (amount: number, currency?: string): string => {
  const formattedAmount = formatLargeNumber(amount);

  if (!currency) {
    return formattedAmount;
  }

  // Add currency symbol prefix for common currencies
  const currencySymbols: Record<string, string> = {
    USD: "$",
    EUR: "€",
    GBP: "£",
    JPY: "¥",
    CNY: "¥",
    CAD: "C$",
    AUD: "A$",
  };

  const symbol = currencySymbols[currency.toUpperCase()];
  if (symbol) {
    return `${symbol}${formattedAmount} ${currency.toUpperCase()}`;
  }

  return `${formattedAmount} ${currency.toUpperCase()}`;
};

/**
 * Formats a project volume value with currency
 * @param value - The project volume value
 * @param currency - The currency code
 * @returns Formatted string or null if no value
 */
export const formatProjectVolume = (
  value?: number | null,
  currency?: string | null
): string | null => {
  if (!value && value !== 0) {
    return null;
  }

  return formatCurrency(value, currency || undefined);
};
